"use client";

import {
  type HTMLAttributes,
  useRef,
  useState,
  type DragEvent,
  type ChangeEvent,
  type FC,
  type Ref,
} from "react";
import { mergeClasses } from "@huub-web/utils";

import type { ClassNameProp, IconProp } from "../../props";
import { Card } from "../Card";
import { Flex } from "../Flex";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { List } from "../List";
import { ListRow } from "../ListRow";
import { IconButton } from "../IconButton";
import { useHover } from "../../hooks/useHover";
import { ProgressBar } from "../ProgressBar";

import {
  hiddenInputStyle,
  contentStyle,
  cardContainerStyle,
} from "./UploadCard.css";

export type UploadCardProps = HTMLAttributes<HTMLButtonElement> &
  ClassNameProp &
  IconProp & {
    /**
     * Title text for the upload card
     */
    title?: string;
    /**
     * Body text for the upload card
     */
    body?: string;
    /**
     * Callback when files are uploaded
     */
    onUpload?: (files: File[]) => void;
    /**
     * Accepted MIME types
     */
    acceptedTypes?: string[];
    /**
     * Allow multiple file selection
     */
    multiple?: boolean;
    /**
     * Disable the upload card
     */
    disabled?: boolean;
    /**
     * Show loading state
     */
    loading?: boolean;
    /**
     * Whether to show the list of uploaded files
     * @default true
     */
    showFileList?: boolean;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLButtonElement>;
  };

/**
 * A card that allows the user to upload files.
 */
export const UploadCard: FC<UploadCardProps> = ({
  title,
  body,
  onUpload,
  acceptedTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ],
  disabled,
  loading,
  multiple,
  icon = "documentUpload",
  showFileList = true,
  className,
  children,
  ref,
  ...props
}) => {
  const { hasHover, handleEnter, handleLeave } = useHover();

  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const filesRef = useRef<File[]>([]);
  const [files, setFiles] = useState<File[]>([]);

  const handleFiles = (uploadedFiles: FileList | null) => {
    if (uploadedFiles) {
      let filteredFiles = Array.from(uploadedFiles).filter((file) =>
        acceptedTypes.includes(file.type),
      );

      if (!multiple) {
        filteredFiles = [filteredFiles[0]];
      }

      onUpload?.(filteredFiles);

      filesRef.current = [...filesRef.current, ...filteredFiles];
      setFiles(filesRef.current);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    e.target.value = "";
  };

  return (
    <Card
      as="button"
      ref={ref}
      padding="default"
      className={mergeClasses(cardContainerStyle, className)}
      contentClassName={contentStyle}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      disabled={disabled}
      variant="primary"
      border="dashed"
      gap="m"
      overflowHidden
      {...props}
    >
      {loading && <ProgressBar variant="infinite" position="absolute" />}

      <input
        ref={inputRef}
        type="file"
        className={hiddenInputStyle}
        multiple
        accept={acceptedTypes.join(",")}
        onChange={handleInputChange}
        autoComplete="off"
        data-1p-ignore
        disabled={disabled}
      />

      <Flex inline gap="s" align="center">
        <IconButton
          icon={isDragging ? "add" : icon}
          variant="tertiary"
          as="div"
          rounded
          active={hasHover && !disabled}
          disabled={disabled}
        />

        <Flex direction="column" gap="xxs2">
          {title && (
            <Text markdown variant="bodyL">
              {title}
            </Text>
          )}
          {body && (
            <Text markdown variant="bodyS" color="currentColor50">
              {body}
            </Text>
          )}
        </Flex>
      </Flex>

      {showFileList && files.length > 0 && (
        <List variant="primary">
          {files.map((file, i) => (
            <ListRow
              key={i}
              variant="primary"
              leading={<Icon icon="document" size="s2" />}
              size="m"
            >
              {file.name}
            </ListRow>
          ))}
        </List>
      )}

      {children}
    </Card>
  );
};
